import { Fragment } from 'react';

import { Money, formatMoney, toMoney } from '@betterment/core';
import { Separator } from '@betterment/samba-react';
import { ActivityDrawerLayout } from '@betterment/shared-components/src/components/activity/components/drawer/common/ActivityDrawerLayout';
import { DrawerListRow } from '@betterment/shared-components/src/components/activity/components/drawer/common/DrawerListRow';
import { formatEnumLabel } from '@betterment/shared-components/src/utils/StringHelpers';

import { ActivityItemFragment } from '../../../../graphql/fragments/activityItem.generated';
import { NarrowTypename } from '../../../../graphql/utils/narrow-typename';
import { ActivityReportsUrl } from '../common/ActivityReportsUrl';
import { StatusSection } from '../common/StatusSection';
import { TradesList } from '../common/trades/TradesList';

const sumToMoney = (trades: { amount: Money }[]): Money => {
  return toMoney(trades.map(t => t.amount.cents).reduce((sum, cents) => sum + cents, 0));
};

const attributionTooltips: Record<string, string | undefined> = {
  'Tax loss harvest sales': `Tax-loss harvesting helps lower taxes by selling investments at a loss to offset gains. We reinvest the proceeds in similar assets, so funds stay invested while capturing the loss.`,
  'Rebalance sales': `Rebalancing helps keep this portfolio aligned with its target allocation. If drift exceeds the portfolio’s tolerance, we’ll sell assets with the intention to minimize the tax impact.`,
};

type TradeGroup = NarrowTypename<ActivityItemFragment, 'ActivityItemTradeGroup'>;
export type TradeItem = TradeGroup['purchases'][number];

export const Trade: React.FC<{
  item: TradeGroup;
}> = ({ item }) => {
  const unattributedPurchases: TradeItem[] = [];
  const unattributedSales: TradeItem[] = [];
  const groupedAttributedTrades: Record<string, TradeItem[]> = {};

  item.purchases.forEach(trade => {
    if (!trade.tradeAttribution) {
      unattributedPurchases.push(trade);
    } else {
      const key = `${formatEnumLabel(trade.tradeAttribution)} purchases`;
      (groupedAttributedTrades[key] ??= []).push(trade);
    }
  });

  item.sales.forEach(trade => {
    if (!trade.tradeAttribution) {
      unattributedSales.push(trade);
    } else {
      const key = `${formatEnumLabel(trade.tradeAttribution)} sales`;
      (groupedAttributedTrades[key] ??= []).push(trade);
    }
  });

  return (
    <ActivityDrawerLayout title="Trading Activity">
      <DrawerListRow label="Account" value={item.subAccount?.name} />
      <Separator />
      {item.statuses.length > 0 && (
        <>
          <StatusSection statuses={item.statuses} />
          <Separator />
        </>
      )}
      {Object.entries(groupedAttributedTrades)
        .sort(([a], [b]) => b.localeCompare(a))
        .map(([attribution, trades]) => {
          const toolTipMessage = attributionTooltips[attribution];

          return (
            <Fragment key={attribution}>
              <TradesList
                title={attribution}
                items={trades}
                subTitle={formatMoney(sumToMoney(trades))}
                toolTipMessage={toolTipMessage}
              />
              <Separator />
            </Fragment>
          );
        })}
      {unattributedSales.length > 0 && (
        <>
          <TradesList title="Sales" items={unattributedSales} subTitle={formatMoney(sumToMoney(unattributedSales))} />
          <Separator />
        </>
      )}
      {unattributedPurchases.length > 0 && (
        <>
          <TradesList
            title="Purchases"
            items={unattributedPurchases}
            subTitle={formatMoney(sumToMoney(unattributedPurchases))}
          />
          <Separator />
        </>
      )}
      {item.activityReportsUrl && <ActivityReportsUrl url={item.activityReportsUrl} />}
    </ActivityDrawerLayout>
  );
};
